'use client';

import { useCallback, useMemo, useState } from 'react';

import { useGetWorkspaceIndustriesQuery } from '@/features/workspaces/api/workspacesApi';
import { AutocompleteAsync } from '@web/ui';

import type { WorkspaceIndustryOption } from '@/features/workspaces/types';

type IndustrySelectFieldProps = {
  value: WorkspaceIndustryOption | null;
  onChange: (value: WorkspaceIndustryOption | null) => void;
  error?: string;
  disabled?: boolean;
};

export function IndustrySelectField({
  value,
  onChange,
  error,
  disabled = false,
}: IndustrySelectFieldProps) {
  const { data: industriesResponse, isLoading } =
    useGetWorkspaceIndustriesQuery();
  const [inputValue, setInputValue] = useState('');

  const industryOptions = useMemo(() => {
    if (
      !industriesResponse ||
      industriesResponse.success !== true ||
      !Array.isArray(industriesResponse.data)
    ) {
      return [] as WorkspaceIndustryOption[];
    }

    return industriesResponse.data;
  }, [industriesResponse]);

  const loadIndustryOptions = useCallback(
    async ({ query }: { query: string; page: number }) => {
      const normalizedQuery = query.trim().toLowerCase();
      const options = normalizedQuery
        ? industryOptions.filter((option) =>
            option.label.toLowerCase().includes(normalizedQuery),
          )
        : industryOptions;

      return {
        options,
        hasMore: false,
      };
    },
    [industryOptions],
  );

  return (
    <AutocompleteAsync<WorkspaceIndustryOption>
      value={value}
      loadOptions={loadIndustryOptions}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selectedValue) =>
        option.id === selectedValue.id
      }
      onChange={(_event, nextValue) => onChange(nextValue)}
      inputValue={inputValue}
      onInputChange={(_event, nextValue) => setInputValue(nextValue)}
      disabled={disabled || isLoading}
      label="Industry"
      placeholder="Choose one"
      noOptionsText="No industries found"
      textFieldProps={{
        error: Boolean(error),
        helperText: error,
      }}
    />
  );
}
